import React, { useContext, useState, useMemo } from "react";
import {
    Button,
    FlexContainer,
    StandardModal,
    StandardModalActionGroup,
    StandardModalInfoCard,
    StandardModalSection,
    StandardModalTable,
    StandardModalTableScroll,
    StandardModalTableSection,
    StandardModalText
} from "src/components";
import { NewCampaignContext } from "./NewCampaignContext";
import { USER_GROUP } from "src/constants/perks";
import Checkbox from "src/scenes/EmployeeList/components/Checkbox";
import { getEmailAudienceDisplayName } from "../../constants";

function HeaderCard(props) {
    const { audienceName, quantity } = props;
    return (
        <StandardModalInfoCard
            background="#F3F5FB"
            padding="1rem"
            style={{ width: "257px" }}
        >
            <FlexContainer alignItems="flex-start">
                <StandardModalText size="20px" weight="800" color="#612684">{audienceName}</StandardModalText>
                <StandardModalText size="14px" weight="500" color="#171A22">Selected</StandardModalText>
            </FlexContainer>
            <StandardModalText size="30px" weight="900" color="#171A22">{quantity}</StandardModalText>
        </StandardModalInfoCard>
    );
}

function TableHeadings({
    isAllSelected,
    toggleIsAllSelected
}) {
    return (
        <tr>
            <th style={{ textAlign: "center", width: "50px" }}>
                <Checkbox
                    checked={isAllSelected}
                    onClick={toggleIsAllSelected}
                />
            </th>
            <th>First Name</th>
            <th>Last Name</th>
            <th>Email</th>
            <th>Mobile</th>
            <th>Job Title</th>
        </tr>
    );
}

function TableRow({
    contact,
    isSelected,
    toggleContactUuid
}) {
    return (
        <tr>
            <td style={{ textAlign: "center", width: "3rem" }}>
                <Checkbox
                    checked={isSelected}
                    onClick={() => toggleContactUuid(contact.uuid)}
                />
            </td>
            <td>{contact.firstName}</td>
            <td>{contact.lastName}</td>
            <td>{contact.email}</td>
            <td>{contact.mobileNumber}</td>
            <td>{contact.reference}</td>
        </tr>
    );
}

function ContactsTable({
    audience,
    selectedContactUuids,
    isAllSelected,
    toggleIsAllSelected,
    toggleContactUuidSelected
}) {
    return (
        <StandardModalTableSection>
            <StandardModalTableScroll maxHeight="min(32rem, calc(100vh - 26rem))">
                <StandardModalTable minWidth="760px">
                    <thead>
                        <TableHeadings
                            isAllSelected={isAllSelected}
                            toggleIsAllSelected={toggleIsAllSelected}
                        />
                    </thead>
                    <tbody>
                        { audience.contacts.map((contact, index) => (
                            <TableRow
                                key={contact.uuid || index}
                                contact={contact}
                                isSelected={selectedContactUuids.includes(contact.uuid)}
                                toggleContactUuid={toggleContactUuidSelected}
                            />
                        ))}
                    </tbody>
                </StandardModalTable>
            </StandardModalTableScroll>
        </StandardModalTableSection>
    );
}

export function EditAudienceSelection() {
    const {
        recipientContactUuids,
        selectionEditAudience,
        setRecipientContactUuids,
        closeEditSelection
    } = useContext(NewCampaignContext);

    const audienceContactUuids = useMemo(() => {
        return selectionEditAudience.contacts.map(contact => contact.uuid);
    }, [selectionEditAudience]);

    const existingSelectedUuids = recipientContactUuids.filter(uuid => audienceContactUuids.includes(uuid));

    const [selectedContactUuids, setSelectedContactUuids] = useState(existingSelectedUuids);

    const allSelected = useMemo(() => {
        return selectedContactUuids.length === audienceContactUuids.length;
    }, [selectedContactUuids]);

    const toggleIsAllSelected = () => {
        if (allSelected) {
            setSelectedContactUuids([]);
        } else {
            setSelectedContactUuids(audienceContactUuids);
        }
    };

    const toggleContactUuidSelected = contactUuid => {
        setSelectedContactUuids(prev => (
            prev.includes(contactUuid) ?
                prev.filter(uuid => uuid !== contactUuid) :
                [...prev, contactUuid]
        ));
    };

    const isDefaultAudience = [USER_GROUP.EMPLOYEES, USER_GROUP.CLIENTS].includes(selectionEditAudience.name);
    const audienceName = getEmailAudienceDisplayName(selectionEditAudience.name);
    const dialogName = isDefaultAudience ?
        `Me Business ${audienceName}` :
        selectionEditAudience.name;

    const applySelections = () => {
        const otherAudiences = recipientContactUuids.filter(uuid => !audienceContactUuids.includes(uuid));
        setRecipientContactUuids([...otherAudiences, ...selectedContactUuids]);
        closeEditSelection();
    };

    return (
        <StandardModal
            open={true}
            onClose={closeEditSelection}
            widthMode="table"
            title={dialogName}
            footer={(
                <StandardModalActionGroup $pushRight>
                    <Button
                        type="button"
                        onClick={closeEditSelection}
                        width="140px"
                        height="38px"
                        border="1px solid #000"
                        color="#000000"
                        style={{ borderRadius: "7px", boxShadow: "none" }}
                    >
                        Close
                    </Button>
                    <Button
                        type="button"
                        onClick={applySelections}
                        width="140px"
                        height="38px"
                        backgroundColor="#171A22"
                        border="1px solid #171A22"
                        color="#FFFFFF"
                        style={{ borderRadius: "7px", boxShadow: "none" }}
                    >
                        Save
                    </Button>
                </StandardModalActionGroup>
            )}
        >
            <StandardModalSection gap="1rem">
                <HeaderCard audienceName={audienceName} quantity={selectedContactUuids.length} />
                <ContactsTable
                    audience={selectionEditAudience}
                    selectedContactUuids={selectedContactUuids}
                    isAllSelected={allSelected}
                    toggleIsAllSelected={toggleIsAllSelected}
                    toggleContactUuidSelected={toggleContactUuidSelected}
                />
            </StandardModalSection>
        </StandardModal>
    );
}
